/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import { TextField, Grid, Box, Button, Typography, Checkbox, FormControl, FormControlLabel, debounce, Stack, Alert, AlertTitle } from '@mui/material';
import MomentUtils from '@date-io/moment';
import moment from 'moment-timezone';
import Region from '../../constants/region';
import { showMessage, addRoute, editRoute } from '../../store/actions';
import { CustomGoogleMap } from '../../custom-widgets';
import SearchableSelectAll from '../../custom-widgets/SearchableSelectAll';
import ProducerPickupSequence from '../../custom-widgets/ProducerPickupSequence';
import {
    arrayToMap,
    getGoogleMapsURL,
    getDefaultLatitude,
    getDefaultLongitude,
    getDefaultZoom,
    getRole,
    milesToMeters,
    roundMetersToMiles,
    //
} from '../../../utils';
import { LocalizationConsumer } from '../../localization/LocalizationContext';
import { getRoute } from '../../repository';
import ConfirmationDialog from '../../main/pages/settings/notifications/ConfirmationDialog';

const disableProcessorChange = (role) => {
    switch (role) {
        case 'admin':
            return false;
        default:
            return true;
    }
};

const formValidWithoutProcessorSelect = (role, region) => {
    if ([Region.PRAIRIE].includes(region) && ['transport'].includes(role)) {
        return true;
    }
    if ([Region.MMPA].includes(region) && ['admin'].includes(role)) {
        return true;
    }
    return false;
};

const formSx = {
    root: {
        flexGrow: 1,
    },
    paper: {
        display: 'flex',
        padding: (theme) => theme.spacing(2),
        textAlign: 'center',
        color: (theme) => theme.palette.text.secondary,
    },
    map: {
        height: '400px',
        justifyContent: 'center',
        margin: '2%',
    },
    list: {
        maxHeight: '280px',
        overflowY: 'scroll',
    },
    submit: {
        display: 'flex',
        justifyContent: 'center',
        gridGap: '30px',
        mt: (theme) => theme.spacing(4),
    },
};

function RouteForm({ producers, processors, haulingCompanies, region, editDetails }) {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const history = useHistory();
    const hasComponents = [Region.UDA].includes(region);
    const has24HourClock = [Region.MMPA].includes(region);
    const hasKosher = [Region.UNC, Region.BONGARDS].includes(region);
    const hasTypeSelector = [Region.PEI, Region.NL, Region.RF].includes(region);
    const hasMultipleHaulers = ![Region.MMPA].includes(region);
    const hasExpectedMiles = [Region.PSC].includes(region);
    const producerMap = arrayToMap(producers, 'id');
    const processorMap = arrayToMap(processors, 'id');
    const haulingMap = arrayToMap(haulingCompanies, 'id');
    const [name, setName] = useState('');
    const [producerIds, setProducerIds] = useState([]);
    const [producerPickupSequence, setProducerPickupSequence] = useState([]);
    const [producerOptions, setProducerOptions] = useState(producers);
    const [processorIds, setProcessorIds] = useState([]);
    const [haulingIds, setHaulingIds] = useState([]);
    const [amount, setAmount] = useState('');
    const [startTime, setStartTime] = useState(moment('09:00', 'HH:mm'));
    const [endTime, setEndTime] = useState(moment('17:00', 'HH:mm'));
    const [dairyType, setDairyType] = useState({ name: 'Milk', value: 'milk' });
    const [category, setCategory] = useState({});
    const [kosher, setKosher] = useState(false);
    const [protein, setProtein] = useState(undefined);
    const [expectedMiles, setExpectedMiles] = useState(undefined);
    const [isValid, setIsValid] = useState(false);
    const [routeNameError, setRouteNameError] = useState();
    const [latitude] = useState(getDefaultLatitude(region));
    const [longitude] = useState(getDefaultLongitude(region));
    const [overrideZoom] = useState(getDefaultZoom(region));
    const [selectedType] = useState('milk');
    const [expectedAmountDialogOpen, setExpectedAmountDialogOpen] = useState(false);
    const [producerSequenceDialogOpen, setProducerSequenceDialogOpen] = useState(false);
    const [producerSequenceProrateDialogOpen, setProducerSequenceProrateDialogOpen] = useState(false);
    const role = getRole();

    const producerPickupSequenceTotal = producerPickupSequence.reduce((acc, next) => acc + Number(next.amount), 0);

    useEffect(() => {
        const getCategoryName = (value) => {
            if (value === 'member_producer') return 'Member Producer';
            if (value === 'outside_milk') return 'Outside Milk';
            if (value === 'comingled') return 'Co-mingled';
        };

        if (editDetails) {
            setStartTime(moment(editDetails.start_time, 'HH:mm'));
            setEndTime(moment(editDetails.end_time, 'HH:mm'));
            setAmount(editDetails.amount);
            setDairyType({ name: editDetails.type_of_fluid, value: editDetails.type_of_fluid.toLowerCase() });
            setName(editDetails.name);
            setHaulingIds(editDetails.hauling_companies);
            setProducerIds(editDetails.users.map((id) => producerMap[id]?.id));
            setProcessorIds(editDetails.processors?.map((id) => processorMap[id]?.id).filter((id) => id !== undefined) || []);
            setProtein(editDetails.protein ? editDetails.protein.toString() : '');
            setCategory(editDetails.category && { name: getCategoryName(editDetails.category), value: editDetails.category });
            setKosher(editDetails?.kosher ?? false);
            setExpectedMiles(editDetails?.expected_driven_distance_meters ? roundMetersToMiles(editDetails.expected_driven_distance_meters) : null);
            if (editDetails.producer_pickup_sequence) {
                setProducerPickupSequence(editDetails?.producer_pickup_sequence?.filter(({ producer_id: id }) => producerMap[id]).map(({ producer_id: id, expected_amount }) => ({ id, amount: expected_amount, name: producerMap[id].name })) ?? []);
            } else {
                setProducerPickupSequence(editDetails.users.map((id) => ({ id, amount: 0, name: producerMap[id].name })));
            }
        }
    }, [editDetails]);

    useEffect(() => {
        const validateForm = () => {
            if (routeNameError) return false;
            if (!name.length) return false;
            if (!producerIds.length) return false;
            if (!processorIds.length && !formValidWithoutProcessorSelect(role, region)) return false;
            if (!haulingIds.length) return false;
            if (!amount) return false;
            if (!category?.value) return false;

            return true;
        };

        setIsValid(validateForm());
    }, [routeNameError, name, producerIds, processorIds, haulingIds, amount, category, producerPickupSequenceTotal]);

    useEffect(() => {
        const categoryFilters = {
            member_producer: (p) => p.milk_category === 'member_producer',
            outside_milk: (p) => p.milk_category === 'outside_milk',
            comingled: (p) => ['outside_milk', 'member_producer'].includes(p.milk_category),
        };

        const filterByCategory = categoryFilters[category?.value] || (() => false);
        const filteredProducers = producers.filter(filterByCategory);

        setProducerOptions(filteredProducers);
    }, [producers, category]);

    const checkUniqueness = useCallback(
        debounce(async (routeName) => {
            const existingRoutes = await getRoute({ name: routeName, filterDeleted: true });
            if (existingRoutes.length > 0) {
                setRouteNameError('Route name already exists');
                return;
            }
            if (existingRoutes.err) {
                setRouteNameError('Could not validate route name');
                return;
            }
            setRouteNameError();
        }, 750),
        []
    );

    const handleRouteChange = async (event) => {
        const routeName = event.target.value;
        setName(routeName);
        if (routeName) checkUniqueness(routeName);
    };

    const handleCategoryChange = (_event, value) => {
        setCategory(value);
    };

    const handleKosherChange = (event) => {
        setKosher(event.target.checked);
    };

    const handleProducersChange = (_event, value) => {
        setProducerIds(value.map((v) => v.id));
        setProducerPickupSequence(value.map(({ id }) => ({ id, name: producerMap[id].name, amount: producerPickupSequence.find((s) => s.id === id)?.amount ?? 0 })));
    };

    const handleProcessorsChange = (_event, value) => {
        setProcessorIds(value.map((v) => v.id));
    };

    const handleHaulingChange = (_event, value) => {
        if (value.length === 0) {
            setHaulingIds([]);
            return;
        }

        if (hasMultipleHaulers) {
            const mappedHaulingIds = value.map((v) => v.id);
            setHaulingIds(mappedHaulingIds);

            return;
        }

        const haulingId = value[value.length - 1].id;
        setHaulingIds([haulingId]);
    };

    const handleAmountChange = (event) => {
        let value = parseInt(event.target.value);
        if (isNaN(value)) {
            setAmount(0);
        } else {
            value = value >= 0 ? value : 0;
            setAmount(value);
        }
    };

    const handleStartTimeChange = (date) => {
        setStartTime(date);
    };

    const handleEndTimeChange = (date) => {
        setEndTime(date);
    };

    const handleDairyTypeChange = (_event, value) => {
        setDairyType(value);
    };

    const handleDraggableListChange = (selected) => {
        setProducerIds(selected.map(({ id }) => id));
        setProducerPickupSequence(selected);
    };

    const handleSetExpectedAmount = () => {
        setAmount(producerPickupSequenceTotal);
    };

    const handleCalculatePickupSequenceAmounts = () => {
        const avg = Math.round(Number(amount) / producerPickupSequence.length);
        const diff = Number(amount) - avg * producerPickupSequence.length; // Geting rounding descrepency
        setProducerPickupSequence(
            producerPickupSequence.map((pickup, i) => ({
                ...pickup,
                amount: i === producerPickupSequence.length - 1 ? avg + diff : avg, // Adjust last average to account for any rounding errors
            }))
        );
    };

    const handleCalculatePickupProratedSequenceAmounts = () => {
        const scaledAmount = Math.round(Number(amount) * 100);

        const originalAmount = producerPickupSequence.reduce((acc, pickup) => acc + Number(pickup.amount), 0);
        const ratios = producerPickupSequence.map((pickup) => Number(pickup.amount) / originalAmount);

        const newAmounts = ratios.map((ratio) => Math.floor(ratio * scaledAmount));
        const newTotal = newAmounts.reduce((acc, amt) => acc + amt, 0);

        const remainder = scaledAmount - newTotal;
        const fractionalParts = ratios
            .map((ratio, index) => ({
                index,
                fraction: ratio * scaledAmount - Math.floor(ratio * scaledAmount),
            }))
            .sort((a, b) => b.fraction - a.fraction);

        const finalAmounts = [...newAmounts];
        for (let i = 0; i < remainder; i++) {
            finalAmounts[fractionalParts[i % fractionalParts.length].index] += 1;
        }

        setProducerPickupSequence(
            producerPickupSequence.map((pickup, i) => ({
                ...pickup,
                amount: +(finalAmounts[i] / 100).toFixed(2),
            }))
        );
    };

    const handleProteinChange = (event) => {
        const { value } = event.target;
        setProtein(!value || (!isNaN(value) && !isNaN(parseFloat(value))) ? value : protein);
    };

    const handleExpectedMilesChange = (event) => {
        const value = parseInt(event.target.value);
        if (isNaN(value)) {
            setExpectedMiles(null);
        } else {
            setExpectedMiles(value);
        }
    };

    const transformProducers = (details) => {
        return details
            .filter((value) => value.type_of_fluid === selectedType)
            .map((value) => ({
                ...value,
                label: value.label,
                latitude: value.latitude,
                longtitude: value.longitude,
                isSelected: producerIds ? producerIds.includes(value.id) : false,
            }));
    };

    const addProducerFromMap = (value) => {
        let updatedProducers;
        if (value.isSelected) {
            updatedProducers = producerIds
                .filter((id) => {
                    return id !== value.id;
                })
                .map((id) => producerMap[id]);
        } else {
            updatedProducers = producerIds.map((id) => producerMap[id]).concat(value);
        }

        setProducerIds(updatedProducers.map((v) => v.id));
        setProducerPickupSequence(updatedProducers.map(({ id }) => ({ id, name: producerMap[id].name, amount: producerPickupSequence.find((s) => s.id === id)?.amount ?? 0 })));
    };

    const getModel = () => ({
        name,
        users: producerIds,
        processors: processorIds,
        hauling_companies: haulingIds,
        amount,
        start_time: startTime.format('HH:mm'),
        end_time: endTime.format('HH:mm'),
        type_of_fluid: dairyType.value,
        category: category?.value,
        producer_pickup_sequence: producerPickupSequence.map(({ id, amount: expected_amount }) => ({ producer_id: id, expected_amount: Number(expected_amount) })),
        kosher,
        ...(hasComponents && protein && { protein: parseFloat(protein) }),
        ...(hasExpectedMiles && { expected_driven_distance_meters: milesToMeters(expectedMiles) }),
    });

    const invalidateRoutesTable = () => {
        queryClient.invalidateQueries({ queryKey: 'routes-table' });
    };

    const post = () => {
        const model = getModel();

        dispatch(addRoute(model))
            .then(() => {
                dispatch(showMessage({ message: 'Successfully Added Route' }));
                invalidateRoutesTable();
                history.replace({ pathname: '/list-schedule-routes' });
            })
            .catch(() => {
                dispatch(showMessage({ message: 'Could not add Route' }));
            });
    };

    const patch = () => {
        const { _id } = editDetails;
        const model = getModel();

        dispatch(editRoute(model, _id))
            .then(() => {
                dispatch(showMessage({ message: 'Successfully Edited Route' }));
                invalidateRoutesTable();
                history.replace({ pathname: '/list-schedule-routes' });
            })
            .catch(() => {
                dispatch(showMessage({ message: 'Could not edit Route' }));
            });
    };

    const cancel = () => {
        history.replace({ pathname: '/list-schedule-routes' });
    };

    return (
        <LocalizationConsumer>
            {(localization) => (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <Box sx={formSx.root}>
                        <Grid container alignItems="center" spacing={3}>
                            <Grid item xs={hasKosher ? 10 : hasComponents ? 6 : 12}>
                                <TextField
                                    required
                                    value={name}
                                    onChange={handleRouteChange}
                                    label="Route Name"
                                    fullWidth
                                    margin="normal"
                                    variant="standard"
                                    error={!!routeNameError}
                                    helperText={routeNameError}
                                    // wrap
                                />
                            </Grid>
                            {hasKosher && (
                                <Grid item xs={2}>
                                    <FormControl>
                                        <FormControlLabel sx={{ mt: (theme) => theme.spacing(4) }} label="Kosher" labelPlacement="start" control={<Checkbox checked={kosher} onChange={handleKosherChange} />} />
                                    </FormControl>
                                </Grid>
                            )}
                            {hasComponents && (
                                <Grid item xs={6}>
                                    <TextField value={protein} onChange={handleProteinChange} label="Expected Protein" variant="standard" fullWidth />
                                </Grid>
                            )}
                            <Grid item xs={6} sm={3}>
                                <TextField required type="number" value={amount} onChange={handleAmountChange} label="Expected Amount" variant="standard" fullWidth inputProps={{ min: 0 }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TimePicker label="Start Time" value={startTime} fullWidth onChange={handleStartTimeChange} ampm={!has24HourClock} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TimePicker label="End Time" value={endTime} fullWidth onChange={handleEndTimeChange} ampm={!has24HourClock} />
                            </Grid>
                            {hasTypeSelector && (
                                <Grid item xs={6} sm={3}>
                                    <SearchableSelectAll
                                        label={'Type'}
                                        onChange={handleDairyTypeChange}
                                        value={dairyType}
                                        fullWidth
                                        options={[
                                            { name: 'Milk', value: 'milk' },
                                            { name: 'Cream', value: 'cream' },
                                        ]}
                                    ></SearchableSelectAll>
                                </Grid>
                            )}
                            {!hasTypeSelector && (
                                <Grid item xs={6} sm={3}>
                                    <SearchableSelectAll
                                        required
                                        label={'Category'}
                                        onChange={handleCategoryChange}
                                        value={category}
                                        fullWidth
                                        options={[
                                            { name: 'Member Producer', value: 'member_producer' },
                                            { name: 'Outside Milk', value: 'outside_milk' },
                                            { name: 'Co-mingled', value: 'comingled' },
                                        ]}
                                    ></SearchableSelectAll>
                                </Grid>
                            )}
                            {hasExpectedMiles && (
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        value={expectedMiles ?? undefined}
                                        onChange={handleExpectedMilesChange}
                                        label="Expected Miles"
                                        variant="standard"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        fullWidth
                                        //
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <SearchableSelectAll required value={processorIds.map((id) => processorMap[id])} options={processors} onChange={handleProcessorsChange} label={`${localization.general.processor_plural}`} multiple chipLimit={5} disabled={disableProcessorChange(role)} />
                            </Grid>
                            <Grid item xs={12}>
                                <SearchableSelectAll required value={producerIds.map((id) => producerMap[id])} options={producerOptions} onChange={handleProducersChange} label={'Producers'} multiple chipLimit={5} />
                                {producerIds.length === 0 && (
                                    <Typography variant="caption" color="error">
                                        At least one producer is required
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <SearchableSelectAll
                                    required
                                    value={haulingIds.map((id) => haulingMap[id])}
                                    options={haulingCompanies}
                                    onChange={handleHaulingChange}
                                    label={hasMultipleHaulers ? localization.general.hauling_plural : localization.general.hauling_singular}
                                    multiple
                                    chipLimit={5}
                                    disabled={editDetails && haulingCompanies?.length === 1}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6">Producer Order</Typography>
                                <ProducerPickupSequence fields={producerPickupSequence} onChange={handleDraggableListChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={formSx.map}>
                                    <CustomGoogleMap
                                        googleMapURL={getGoogleMapsURL()}
                                        loadingElement={<div style={{ height: '100%' }} />}
                                        containerElement={<div style={{ height: '400px' }} />}
                                        mapElement={<div style={{ height: '100%' }} />}
                                        lat={latitude}
                                        lng={longitude}
                                        draggable={false}
                                        overrideZoom={overrideZoom}
                                        producerLocations={transformProducers(producerOptions)}
                                        pinFunction={(value) => addProducerFromMap(value)}
                                    />
                                </Box>
                            </Grid>
                            {producerPickupSequence.length !== 0 && producerPickupSequenceTotal !== Number(amount) && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity="warning"
                                        action={
                                            <Stack direction="row" spacing={1}>
                                                <Button color="inherit" variant="outlined" size="small" onClick={() => (Number(amount) ? setExpectedAmountDialogOpen(true) : handleSetExpectedAmount())}>
                                                    Set Expected Amount
                                                </Button>
                                                <Button color="inherit" variant="outlined" size="small" onClick={() => (producerPickupSequenceTotal ? setProducerSequenceDialogOpen(true) : handleCalculatePickupSequenceAmounts())}>
                                                    Average Producer Order Amount
                                                </Button>
                                                <Button color="inherit" variant="outlined" size="small" onClick={() => (producerPickupSequenceTotal ? setProducerSequenceProrateDialogOpen(true) : handleCalculatePickupProratedSequenceAmounts())}>
                                                    Prorate Producer Order Amount
                                                </Button>
                                            </Stack>
                                        }
                                    >
                                        <AlertTitle>
                                            {' '}
                                            Expected amount ({amount ?? 0}) does not match producer order ({producerPickupSequenceTotal}).
                                        </AlertTitle>
                                        <br />
                                        Please select an action to correct this.
                                        <br />
                                        <strong>Set Expected Amount</strong> will replace the current expected amount with the sum of the producer order amounts.
                                        <br />
                                        <strong>Average Producer Order Amount</strong> will replace all producer order amounts with an average based on the current expected amount.
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                        <Box sx={formSx.submit}>
                            <Button variant="contained" margin="normal" color="secondary" onClick={cancel}>
                                Cancel
                            </Button>
                            <Button disabled={!isValid || producerPickupSequenceTotal !== amount} variant="contained" color="primary" margin="normal" onClick={editDetails ? patch : post}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                    <ConfirmationDialog
                        open={expectedAmountDialogOpen}
                        message={`This action will replace the current expected amount (${amount}) with the total pickup order (${producerPickupSequenceTotal}). Are you sure you want to proceed?`}
                        onDialogConfirmAction={() => {
                            handleSetExpectedAmount();
                            setExpectedAmountDialogOpen(false);
                        }}
                        onDialogCancelAction={() => setExpectedAmountDialogOpen(false)}
                    />
                    <ConfirmationDialog
                        open={producerSequenceDialogOpen}
                        message={`This action will replace all amounts in the pickup order with an average based on the current expected amount (${amount}). Are you sure you want to proceed?`}
                        onDialogConfirmAction={() => {
                            handleCalculatePickupSequenceAmounts();
                            setProducerSequenceDialogOpen(false);
                        }}
                        onDialogCancelAction={() => setProducerSequenceDialogOpen(false)}
                    />
                    <ConfirmationDialog
                        open={producerSequenceProrateDialogOpen}
                        message={`This action will replace all amounts in the pickup order with a prorated amount based on the current expected amount (${amount}). Are you sure you want to proceed?`}
                        onDialogConfirmAction={() => {
                            handleCalculatePickupProratedSequenceAmounts();
                            setProducerSequenceProrateDialogOpen(false);
                        }}
                        onDialogCancelAction={() => setProducerSequenceProrateDialogOpen(false)}
                    />
                </MuiPickersUtilsProvider>
            )}
        </LocalizationConsumer>
    );
}

export default withRouter(RouteForm);
